import { Clock, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import ContactForm from "@/components/ContactForm";
import PageHero from "@/components/PageHero";
import SEO from "@/components/SEO";
import { CONTACT } from "@/data/site";
import { useLang } from "@/i18n";

const Card = ({ icon: Icon, label, children, href, testid }) => {
  const cls = "flex items-start gap-4 bg-base p-6";
  const inner = (
    <>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-ink/10 bg-surface">
        <Icon className="h-4 w-4 text-gold" />
      </span>
      <div className="min-w-0">
        <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-ink/50">{label}</p>
        <div className="mt-2 text-[15px] leading-relaxed text-ink">{children}</div>
      </div>
    </>
  );
  return href ? (
    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid={testid} className={`${cls} transition-colors hover:bg-surface`}>{inner}</a>
  ) : (
    <div data-testid={testid} className={cls}>{inner}</div>
  );
};

export default function ContactPage() {
  const { t } = useLang();
  return (
    <div data-testid="contact-page">
      <SEO
        metaKey="contact"
        title="Contact — Cere Ofertă Gratuită | HEAVY METAL Buzău"
        description="Contactează HEAVY METAL Buzău: telefon 0753 560 662, WhatsApp sau formular online. Strada Islazului nr. 4A, Buzău. Program L–V 08:00–20:00. Ofertă pentru porți, garduri și structuri metalice."
      />
      <PageHero kicker={t("nav_contact")} title={t("nav_contact")} subtitle={t("ct_sub")} image="/images/servicii-hero.jpg" />

      <section className="border-b border-ink/10 bg-surface" data-testid="contact-cards">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-4">
            <Card icon={Phone} label={t("ct_phone")} href={CONTACT.phoneHref} testid="contact-phone-card">{CONTACT.phone}</Card>
            <Card icon={MessageCircle} label="WhatsApp" href={CONTACT.whatsapp} testid="contact-whatsapp-card">{t("ct_wa")}</Card>
            <Card icon={Mail} label="Email" href={`mailto:${CONTACT.email}`} testid="contact-email-card">{CONTACT.email}</Card>
            <Card icon={Clock} label={t("ct_hours")} testid="contact-hours-card">
              {CONTACT.hours.map((h, i) => (
                <p key={h.days}>{t("days")[i]}: <span className="text-ink/60">{i === 2 ? t("closed") : h.time}</span></p>
              ))}
            </Card>
          </div>
        </div>
      </section>

      <section data-testid="contact-main">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-24 sm:px-6 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-7">
            <h2 className="font-heading text-3xl font-semibold leading-none text-ink md:text-4xl">{t("ct_msg_t")}</h2>
            <p className="mt-4 max-w-lg text-[15px] leading-relaxed text-ink/65">
              {t("ct_msg_x")}
            </p>
            <div className="mt-10 border border-ink/10 bg-base p-6 sm:p-8">
              <ContactForm />
            </div>
          </div>

          <div className="lg:col-span-5">
            <h2 className="font-heading text-3xl font-semibold leading-none text-ink md:text-4xl">{t("ct_atelier")}</h2>
            <div className="mt-4 flex items-start gap-3 text-[15px] text-ink/80" data-testid="contact-address-card">
              <MapPin className="mt-1 h-4 w-4 shrink-0 text-gold" />
              <p>{CONTACT.address}</p>
            </div>
            <div className="mt-8 border border-ink/10 bg-surface p-2" data-testid="contact-map">
              <iframe title="Harta HEAVY METAL" src={CONTACT.mapsUrl} className="h-[420px] w-full" loading="lazy" />
            </div>
            <a
              href={CONTACT.mapsUrl.replace("&output=embed", "")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-directions"
              className="mt-4 inline-flex items-center gap-2 text-[14px] font-semibold text-ink underline decoration-gold decoration-2 underline-offset-[6px] hover:text-gold"
            >
              {t("ct_maps")}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
